/**
 * Phase 2 Facilitation Components for Collaboration Canvas
 * Timer, Private/Incognito Mode, and Dot Voting
 */
import React, { useState, useEffect, useRef } from 'react';
import { 
  Timer, Eye, EyeOff, Vote, Play, Pause, RotateCcw,
  Circle, Trash2
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import { cn } from '../../lib/utils';

// ==================== TIMER COMPONENT (SYNCHRONIZED) ====================
export const FacilitationTimer = ({ 
  onTimerEnd, 
  colors,
  // New props for synchronization
  isTimerRunning,
  syncedTimeLeft,
  timerStartedBy,
  onStartTimer,
  onPauseTimer,
  onResetTimer,
  onSyncTimer,
  currentUserName
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [localTimeLeft, setLocalTimeLeft] = useState(0);
  const [showTimer, setShowTimer] = useState(false);
  const intervalRef = useRef(null);
  const lastSyncRef = useRef(0);

  // Use synced time if available, otherwise local time
  const timeLeft = syncedTimeLeft !== undefined ? syncedTimeLeft : localTimeLeft;
  const isRunning = isTimerRunning !== undefined ? isTimerRunning : false;

  // Start timer (initiates sync)
  const startTimer = () => {
    const totalSeconds = minutes * 60 + seconds;
    if (totalSeconds <= 0) return;
    
    setShowTimer(true);
    setIsOpen(false);
    
    // Emit to other users via parent
    if (onStartTimer) {
      onStartTimer(totalSeconds, currentUserName);
    } else {
      // Fallback to local-only timer
      setLocalTimeLeft(totalSeconds);
    }
  };

  // Pause/Resume
  const togglePause = () => {
    if (onPauseTimer && isRunning) {
      onPauseTimer(timeLeft);
    } else if (onStartTimer && !isRunning && timeLeft > 0) {
      // Resume by syncing current time
      onStartTimer(timeLeft, currentUserName);
    }
  };

  // Reset
  const resetTimer = () => {
    setShowTimer(false);
    setLocalTimeLeft(0);
    if (onResetTimer) {
      onResetTimer();
    }
  };

  // Timer countdown (only the timer owner runs this and syncs to others)
  useEffect(() => {
    if (isRunning && timeLeft > 0 && timerStartedBy === currentUserName) {
      intervalRef.current = setInterval(() => {
        const newTime = timeLeft - 1;
        
        // Sync every 5 seconds or on important events
        const now = Date.now();
        if (now - lastSyncRef.current > 5000 || newTime <= 10 || newTime % 30 === 0) {
          onSyncTimer?.(newTime, true);
          lastSyncRef.current = now;
        }
        
        if (newTime <= 0) {
          clearInterval(intervalRef.current);
          onTimerEnd?.();
          onSyncTimer?.(0, false);
          // Play sound
          try {
            const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleQ0JcLTh2I5EIjl0ss/grkQsRHy348+YPCdDlPb/8tFlQkuI59zxvnBJUJn679GQa1Jdhur9/8SMXWBym+n98rtiXW6V5vzsw3xnYYjn++rQgG1lgOb968J5a2eE5fvp0H9uZoHl++rDem1og+X76tB/bmWB5fvqwnltaIPl++rQf25lgeX76sN5bWeE5fvp0H9uZYHl++rCeW1og+X76tB/bmWB5fvqwnltaIPl++rQf25lgeX76sN5bWeE5fvp0H9uZYHl++rCeW1og+X76tB/bmWB5fvqw3lsaITl++rQf25lgeX76sJ5bWiD5fvq0H9uZYHl++rDeWxohOX76dB/bmWB5fvqwnltaIPl++rQf25lgeX76sN5bGeE5fvp0H9uZYHl++rCeW1og+X76tB/bmaB5fvqw3lsZ4Tl++nQf25lgeX76sJ5bWiD5fvq0IBuZYDl++rDeWxnhOX76dB/bmWB5fvqwnltaIPl++rQgG5lgOX76sN5bGeE5fvp0H9uZYHl++rCeW1og+X76tB/bmWB5fvqw3lsZ4Tl++rQf25lgeX76sJ5bWiD5fvq0H9uZoHl++rDeWxnhOX76tB/bmWB5fvqwnltaIPl++rQf25mgOX76sN5bGeE5fvq');
            audio.play();
          } catch (e) {
            console.log('Could not play timer sound');
          }
        }
      }, 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [isRunning, timeLeft, timerStartedBy, currentUserName, onTimerEnd, onSyncTimer]);

  // Format time display
  const formatTime = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine if this user can start a new timer
  const canStartTimer = !isTimerRunning && syncedTimeLeft === 0;

  return (
    <>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            size="sm" 
            className="gap-2"
            disabled={isTimerRunning && timerStartedBy !== currentUserName}
          >
            <Timer className="w-4 h-4" />
            {isTimerRunning ? 'Timer Running' : 'Timer'}
          </Button>
        </PopoverTrigger>
        <PopoverContent className={cn("w-64", colors?.bgCard)}>
          <div className="space-y-4">
            <h4 className={cn("font-medium", colors?.textPrimary)}>Set Timer</h4>
            {isTimerRunning && timerStartedBy !== currentUserName ? (
              <p className={cn("text-sm", colors?.textMuted)}>
                Timer started by {timerStartedBy}
              </p>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="60"
                    value={minutes}
                    onChange={(e) => setMinutes(parseInt(e.target.value) || 0)}
                    className="w-20 text-center"
                    disabled={isTimerRunning}
                  />
                  <span className={colors?.textMuted}>min</span>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    value={seconds}
                    onChange={(e) => setSeconds(parseInt(e.target.value) || 0)}
                    className="w-20 text-center"
                    disabled={isTimerRunning}
                  />
                  <span className={colors?.textMuted}>sec</span>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setMinutes(5); setSeconds(0); }}
                    disabled={isTimerRunning}
                  >
                    5 min
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setMinutes(10); setSeconds(0); }}
                    disabled={isTimerRunning}
                  >
                    10 min
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => { setMinutes(15); setSeconds(0); }}
                    disabled={isTimerRunning}
                  >
                    15 min
                  </Button>
                </div>
                <Button 
                  onClick={startTimer} 
                  className="w-full"
                  disabled={isTimerRunning}
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Timer
                </Button>
              </>
            )}
          </div>
        </PopoverContent>
      </Popover>

      {/* Floating timer display - visible to all users */}
      {(showTimer || (syncedTimeLeft !== undefined && syncedTimeLeft > 0)) && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 bg-black/90 text-white px-6 py-3 rounded-xl shadow-2xl flex items-center gap-4">
          <span className={cn(
            "text-3xl font-mono font-bold",
            timeLeft <= 10 && timeLeft > 0 && "text-red-400 animate-pulse"
          )}>
            {formatTime(timeLeft)}
          </span>
          {/* Only show controls to the timer starter */}
          {timerStartedBy === currentUserName && (
            <div className="flex gap-1">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={togglePause}
                className="text-white hover:bg-white/20"
              >
                {isRunning ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={resetTimer}
                className="text-white hover:bg-white/20"
              >
                <RotateCcw className="w-4 h-4" />
              </Button>
            </div>
          )}
          {timerStartedBy && (
            <span className="text-xs text-gray-400">by {timerStartedBy}</span>
          )}
          {timeLeft === 0 && (
            <Badge variant="destructive" className="animate-bounce">
              Time&apos;s up!
            </Badge>
          )}
        </div>
      )}
    </>
  );
};


// ==================== PRIVATE MODE COMPONENT ====================
export const PrivateModeToggle = ({ isPrivate, onToggle, colors }) => {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button 
            variant={isPrivate ? "default" : "outline"} 
            size="sm" 
            onClick={onToggle}
            className={cn("gap-2", isPrivate && "bg-amber-500 hover:bg-amber-600")}
          >
            {isPrivate ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            {isPrivate ? 'Private On' : 'Private'}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p>{isPrivate ? 'Your notes are hidden from others until revealed' : 'Enable private mode to hide your notes'}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};


// ==================== DOT VOTING COMPONENT ====================
export const DotVotingPanel = ({ 
  isVoting, 
  onStartVoting, 
  onEndVoting, 
  votesPerPerson = 3,
  onVotesPerPersonChange,
  elements,
  votes,
  onVote,
  colors,
  // New props for enhanced functionality
  isAnonymous = false,
  onAnonymousToggle,
  onClearVotes,
  // New prop to track if voting has ended with results
  hasVoteResults = false
}) => {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [tempVotes, setTempVotes] = useState(votesPerPerson);
  const [tempAnonymous, setTempAnonymous] = useState(isAnonymous);

  const startVoting = () => {
    onVotesPerPersonChange?.(tempVotes);
    // Pass anonymous value directly to avoid async state issues
    onStartVoting?.(tempAnonymous);
    setIsConfigOpen(false);
  };

  return (
    <>
      {!isVoting ? (
        <>
          <Popover open={isConfigOpen} onOpenChange={setIsConfigOpen}>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <Vote className="w-4 h-4" />
                Dot Voting
              </Button>
            </PopoverTrigger>
            <PopoverContent className={cn("w-72", colors?.bgCard)}>
              <div className="space-y-4">
                <h4 className={cn("font-medium", colors?.textPrimary)}>Start Dot Voting</h4>
                <p className={cn("text-sm", colors?.textMuted)}>
                  Each person gets a limited number of votes to distribute among ideas.
                </p>
                <div className="flex items-center gap-2">
                  <span className={cn("text-sm", colors?.textSecondary)}>Votes per person:</span>
                  <Input
                    type="number"
                    min="1"
                    max="10"
                    value={tempVotes}
                    onChange={(e) => setTempVotes(parseInt(e.target.value) || 3)}
                    className="w-20 text-center"
                  />
                </div>
                
                {/* Voting Mode Radio Buttons */}
                <div className="space-y-2">
                  <Label className={cn("text-sm font-medium", colors?.textSecondary)}>
                    Voting Mode
                  </Label>
                  <div className="space-y-2">
                    <label className={cn(
                      "flex items-center gap-3 p-2 rounded-md cursor-pointer border transition-colors",
                      !tempAnonymous ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                    )}>
                      <input
                        type="radio"
                        name="votingMode"
                        checked={!tempAnonymous}
                        onChange={() => setTempAnonymous(false)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex flex-col">
                        <span className={cn("text-sm font-medium", colors?.textPrimary)}>Real-time</span>
                        <span className={cn("text-xs", colors?.textMuted)}>See vote counts as others vote</span>
                      </div>
                    </label>
                    <label className={cn(
                      "flex items-center gap-3 p-2 rounded-md cursor-pointer border transition-colors",
                      tempAnonymous ? "border-blue-500 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                    )}>
                      <input
                        type="radio"
                        name="votingMode"
                        checked={tempAnonymous}
                        onChange={() => setTempAnonymous(true)}
                        className="w-4 h-4 text-blue-600"
                      />
                      <div className="flex flex-col">
                        <span className={cn("text-sm font-medium", colors?.textPrimary)}>Anonymous</span>
                        <span className={cn("text-xs", colors?.textMuted)}>Vote counts hidden until voting ends</span>
                      </div>
                    </label>
                  </div>
                </div>
                
                <Button onClick={startVoting} className="w-full">
                  <Vote className="w-4 h-4 mr-2" />
                  Start Voting
                </Button>
              </div>
            </PopoverContent>
          </Popover>
          
          {/* Show Clear button only when voting has ended and there are results */}
          {hasVoteResults && onClearVotes && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onClearVotes}
              className="gap-1"
            >
              <Trash2 className="w-3 h-3" />
              Clear Results
            </Button>
          )}
        </>
      ) : (
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="bg-green-100 text-green-700">
            <Vote className="w-3 h-3 mr-1" />
            Voting Active {isAnonymous && '(Anonymous)'}
          </Badge>
          <span className={cn("text-sm", colors?.textMuted)}>
            {votes?.remaining || votesPerPerson} votes left
          </span>
          <Button 
            variant="destructive" 
            size="sm" 
            onClick={onEndVoting}
          >
            End Voting
          </Button>
        </div>
      )}
    </>
  );
};


// ==================== VOTE INDICATOR ON ELEMENT ====================
export const VoteIndicator = ({ voteCount, hasVoted, onVote, canVote, colors }) => {
  if (voteCount === 0 && !canVote) return null;

  return (
    <div className="absolute -right-2 -top-2 flex items-center gap-1">
      {canVote && (
        <button
          onClick={(e) => { e.stopPropagation(); onVote(); }}
          className={cn(
            "w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold transition-all",
            hasVoted 
              ? "bg-green-500 text-white scale-110" 
              : "bg-gray-200 text-gray-600 hover:bg-green-100 hover:scale-110"
          )}
        >
          <Circle className={cn("w-3 h-3", hasVoted && "fill-current")} />
        </button>
      )}
      {voteCount > 0 && (
        <Badge 
          variant="secondary" 
          className="bg-green-500 text-white text-xs px-1.5 py-0.5 min-w-[20px] text-center"
        >
          {voteCount}
        </Badge>
      )}
    </div>
  );
};


// ==================== FOLLOW MODE INDICATOR ====================
export const FollowModeIndicator = ({ followingUser, onStopFollowing, colors }) => {
  if (!followingUser) return null;

  return (
    <div 
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 bg-indigo-600 text-white px-4 py-2 rounded-full shadow-lg flex items-center gap-3"
    >
      <span className="text-sm">
        Following <strong>{followingUser.name}</strong>
      </span>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={onStopFollowing}
        className="text-white hover:bg-white/20 h-6 px-2"
      >
        Stop
      </Button>
    </div>
  );
};


// ==================== CONNECTOR LINE COMPONENT ====================
export const ConnectorLine = ({ 
  startElement, 
  endElement, 
  zoom, 
  pan,
  onDelete,
  isSelected,
  onSelect 
}) => {
  if (!startElement || !endElement) return null;

  // Calculate center points
  const startX = startElement.position.x + (startElement.size?.width || 200) / 2;
  const startY = startElement.position.y + (startElement.size?.height || 200) / 2;
  const endX = endElement.position.x + (endElement.size?.width || 200) / 2;
  const endY = endElement.position.y + (endElement.size?.height || 200) / 2;

  // Calculate control points for bezier curve
  const dx = endX - startX;
  const dy = endY - startY;
  const controlOffset = Math.min(Math.abs(dx), Math.abs(dy)) * 0.5;

  const path = `M ${startX} ${startY} 
                C ${startX + controlOffset} ${startY}, 
                  ${endX - controlOffset} ${endY}, 
                  ${endX} ${endY}`;

  return (
    <g onClick={(e) => { e.stopPropagation(); onSelect?.(); }}>
      {/* Invisible wider path for easier clicking */}
      <path
        d={path}
        fill="none"
        stroke="transparent"
        strokeWidth="20"
        style={{ cursor: 'pointer' }}
      />
      {/* Visible path */}
      <path
        d={path}
        fill="none"
        stroke={isSelected ? "#3b82f6" : "#9ca3af"}
        strokeWidth={isSelected ? 3 : 2}
        strokeDasharray={isSelected ? "none" : "5,5"}
        markerEnd="url(#arrowhead)"
      />
      {/* Delete button when selected */}
      {isSelected && (
        <g 
          transform={`translate(${(startX + endX) / 2 - 10}, ${(startY + endY) / 2 - 10})`}
          style={{ cursor: 'pointer' }}
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
        >
          <circle r="12" fill="white" stroke="#ef4444" strokeWidth="2" cx="10" cy="10" />
          <text x="10" y="14" textAnchor="middle" fill="#ef4444" fontSize="14">×</text>
        </g>
      )}
    </g>
  );
};


// SVG defs for arrow markers
export const ConnectorDefs = () => (
  <defs>
    <marker
      id="arrowhead"
      markerWidth="10"
      markerHeight="7"
      refX="9"
      refY="3.5"
      orient="auto"
    >
      <polygon points="0 0, 10 3.5, 0 7" fill="#9ca3af" />
    </marker>
    <marker
      id="arrowhead-selected"
      markerWidth="10"
      markerHeight="7"
      refX="9"
      refY="3.5"
      orient="auto"
    >
      <polygon points="0 0, 10 3.5, 0 7" fill="#3b82f6" />
    </marker>
  </defs>
);

export default {
  FacilitationTimer,
  PrivateModeToggle,
  DotVotingPanel,
  VoteIndicator,
  FollowModeIndicator,
  ConnectorLine,
  ConnectorDefs,
};
